import * as React from "react";
import { Box, Stack, Text } from "@origin-digital/ods-core";
import SidebarItem from "../SideBar/SidebarItem";
import RemoveSupplyContext from "src/context/RemoveSupplyContext";
import { useParams } from "react-router-dom";
import { RequestStatusEnum } from "src/components/Premises/types";
import { RemoveSupplySideBarContentProps } from "./types";

export const RemoveSupplySideBarContent: React.FC<
  RemoveSupplySideBarContentProps
> = ({ showSupportingDocuments, showRemovalDetails }) => {
  const { setActiveSection, activeSection, premiseAddress, draftInfo } =
    React.useContext(RemoveSupplyContext);

  const isNew = draftInfo === null;
  const isDraft =
    draftInfo?.raw_request_status === RequestStatusEnum.DRAFT ||
    draftInfo?.raw_request_status === RequestStatusEnum.DRAFT_ASSIGNED;
  const isSubmitted = !isNew && !isDraft;

  const { requestId } = useParams<{ requestId: string }>();

  //set the active section based on the section that is currently in view
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-50px 0px -70% 0px",
        threshold: 0,
      }
    );

    const sections = [
      "premiseSection",
      "meterAccessSection",
      "agreementSection",
    ];

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [setActiveSection]);

  //scroll to a section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 210;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      {premiseAddress && (
        <Box
          width="full"
          paddingTop="medium"
          paddingBottom={{
            lg: "small",
            xs: "xsmall",
          }}
          paddingX="large"
        >
          <Stack space="xsmall">
            <Text>{premiseAddress}</Text>
          </Stack>
        </Box>
      )}
      {/* Nav links */}
      <Stack space="none">
        {requestId || !isNew ? (
          <>
            <SidebarItem
              name="Premises"
              id={"premiseReadonlySection"}
              onSelect={() => {
                scrollToSection("premiseReadonlySection");
                setActiveSection("premiseReadonlySection");
              }}
              activeSection={activeSection}
            />
            {showRemovalDetails && (
              <SidebarItem
                name="Removal details"
                id={"removeDetailsSection"}
                onSelect={() => {
                  scrollToSection("removeDetailsSection");
                  setActiveSection("removeDetailsSection");
                }}
                activeSection={activeSection}
              />
            )}
            {!isSubmitted && (
              <SidebarItem
                name="Agreement"
                id={"agreementSection"}
                onSelect={() => {
                  scrollToSection("agreementSection");
                  setActiveSection("agreementSection");
                }}
                activeSection={activeSection}
              />
            )}
            {showSupportingDocuments && (
              <SidebarItem
                name="Supporting documents"
                id={"supportingDocumentsSection"}
                onSelect={() => {
                  scrollToSection("supportingDocumentsSection");
                  setActiveSection("supportingDocumentsSection");
                }}
                activeSection={activeSection}
              />
            )}
          </>
        ) : (
          <SidebarItem
            name="Premises"
            id={"premiseSection"}
            onSelect={() => {
              scrollToSection("premiseSection");
              setActiveSection("premiseSection");
            }}
            activeSection={activeSection}
          />
        )}
      </Stack>
    </>
  );
};
